import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Button, Form, Tooltip, Input, Typography } from "antd";
import { InfoCircleOutlined, CheckOutlined } from "@ant-design/icons";
import { userHasRole } from "../../selectors";
import RegistrationConfirmationModal from "../manage-application/RegistrationConfirmationModal";
import PropTypes from "prop-types";
import style from "./SAMLRegForm.module.css";

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

const SAMLRegForm = ({
  submitFunction,
  initialValues,
  formValidation = null,
  handleFormSubmitClick = null,
  isNewApplicationForm = false,
  onValidationChange = null,
}) => {
  const [storedValues, setStoredValues] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const xmlValidator = (value) => {
    if (!value || value.trim() === "") {
      return "Metadata is required";
    }
    if (!value.includes("SPSSODescriptor")) {
      return "Metadata must contain an SPSSODescriptor element";
    }
    return true;
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      definition: initialValues?.definition || "",
    },
  });

  useEffect(() => {
    if (formValidation && trigger) {
      formValidation(() => trigger());
    }
  }, [formValidation, trigger]);

  // Propagate validation state to parent
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const onSubmitRequests = useCallback(
    (submitValues) => {
      if (!submitValues) return;
      submitFunction({ ...initialValues, ...submitValues });
    },
    [submitFunction, initialValues],
  );

  const onSubmit = useCallback(
    (data) => {
      setStoredValues(data);
      if (initialValues) {
        onSubmitRequests(data);
      } else {
        setConfirmationOpen(true);
      }
    },
    [initialValues, onSubmitRequests],
  );

  const onSubmitConfirmation = () => {
    setConfirmationOpen(false);
    onSubmitRequests(storedValues);
  };

  useEffect(() => {
    if (handleSubmit && handleFormSubmitClick) {
      handleFormSubmitClick(() => handleSubmit(onSubmit));
    }
  }, [handleSubmit, handleFormSubmitClick, onSubmit]);

  return (
    <>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Title level={5} className={style["section-header"]}>
          SAML Metadata
        </Title>
        <Form.Item>
          <Paragraph className={style["helper-text"]}>
            Copy and paste the XML metadata of your application{" "}
            <Tooltip
              placement="top"
              title={
                "Your SAML application is described by " +
                "SAML service provider XML metadata, which includes the identifier (entityID) " +
                "and endpoints (AssertionConsumerService). Check your SAML software " +
                "documentation for details on where to find a copy of your metadata."
              }
            >
              <InfoCircleOutlined
                style={{ color: "#1890ff", fontSize: "14px" }}
              />
            </Tooltip>
          </Paragraph>

          <Controller
            name="definition"
            control={control}
            rules={{ validate: xmlValidator }}
            render={({ field }) => (
              <TextArea
                {...field}
                id="definitionField"
                placeholder={`<?xml version="1.0"?>
<md:EntityDescriptor xmlns:md="urn:oasis:names:tc:SAML:2.0:metadata"
     validUntil="2020-11-12T15:47:57Z"
     cacheDuration="PT604800S"
     entityID="https://test.cern.ch">
    <md:SPSSODescriptor AuthnRequestsSigned="false"
        WantAssertionsSigned="false"
        protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol">
        <md:NameIDFormat>
            urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified
        </md:NameIDFormat>
        <md:AssertionConsumerService
            Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST"
            Location="http://test.cern.ch"
            index="1" />
    </md:SPSSODescriptor>
</md:EntityDescriptor>`}
                rows={10}
                status={errors.definition ? "error" : ""}
              />
            )}
          />

          {errors.definition && (
            <Paragraph style={{ color: "red", marginTop: 4 }}>
              {errors.definition.message}
            </Paragraph>
          )}
        </Form.Item>

        {!isNewApplicationForm && (
          <Form.Item>
            <Button
              className={style["submit-button"]}
              type="primary"
              htmlType="submit"
              id="samlSubmitButton"
              disabled={Object.keys(errors).length > 0}
              icon={<CheckOutlined />}
            >
              Submit
            </Button>
          </Form.Item>
        )}
      </Form>

      <RegistrationConfirmationModal
        visible={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onSubmit={onSubmitConfirmation}
      />
    </>
  );
};

SAMLRegForm.propTypes = {
  submitFunction: PropTypes.func,
  initialValues: PropTypes.shape({
    definition: PropTypes.string,
  }),
  formValidation: PropTypes.func,
  handleFormSubmitClick: PropTypes.func,
  isNewApplicationForm: PropTypes.bool,
  onValidationChange: PropTypes.func,
};

export default connect((state) => ({
  initialValues: state.selectedRegistration.registration,
  admin: userHasRole(state, "admin"),
}))(SAMLRegForm);
